import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { Building } from '@app/shared/models/building.interface';
export interface StartMigrationDialogData {
  buildings: Building[];
}

@Component({
  selector: 'app-start-migration-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule
  ],
  template: `
    <h2 mat-dialog-title>Start New Transfer</h2>
    <mat-dialog-content>
      <p class="mb-3 text-sm text-slate-500">Select the building to start a new ble transfer for.</p>
      <mat-form-field appearance="outline" class="w-full">
        <input
          matInput
          type="search"
          placeholder="Search by building name or ID"
          aria-label="Search buildings"
          data-cy="start-migration-building-search"
          [value]="buildingSearchQuery()"
          [matAutocomplete]="auto"
          (input)="updateBuildingSearch($event)"
        />
        <mat-autocomplete
          #auto="matAutocomplete"
          [autoActiveFirstOption]="true"
          (optionSelected)="selectBuilding($event)">
          @for (building of filteredBuildings(); track building.id) {
          <mat-option [value]="building" [attr.data-cy]="'start-migration-building-option-' + building.id">
            {{ building.name }}
          </mat-option>
          }
          @if (!filteredBuildings().length) {
            <mat-option [disabled]="true">No buildings match your search.</mat-option>
          }
        </mat-autocomplete>
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button data-cy="start-migration-dialog-cancel" (click)="dialogRef.close()">Cancel</button>
      <button
        mat-flat-button
        color="primary"
        data-cy="start-migration-dialog-continue"
        [disabled]="!selectedBuilding()"
        (click)="continue()">
        Continue
      </button>
    </mat-dialog-actions>
  `
})
export class StartMigrationDialog {
  readonly data = inject<StartMigrationDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<StartMigrationDialog, Building | undefined>);

  readonly buildingSearchQuery = signal('');
  readonly selectedBuilding = signal<Building | null>(null);
  readonly filteredBuildings = computed(() => {
    const query = this.buildingSearchQuery().trim().toLowerCase();

    if (!query) {
      return this.data.buildings;
    }

    return this.data.buildings.filter((building) =>
      building.name.toLowerCase().includes(query) || String(building.id).includes(query)
    );
  });

  updateBuildingSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.buildingSearchQuery.set(target.value);
    this.selectedBuilding.set(null);
  }

  selectBuilding(event: MatAutocompleteSelectedEvent): void {
    const building = event.option.value as Building;
    this.selectedBuilding.set(building);
    this.buildingSearchQuery.set(building.name);
  }

  continue(): void {
    this.dialogRef.close(this.selectedBuilding() ?? undefined);
  }
}
